import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../config';
import { Printer, User, Barcode } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { canManageVendedorCodigos } from '../utils/userRoles';

function escapeHtml(s) {
    return String(s ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function escapeJsString(s) {
    return String(s ?? '').replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

/** JsBarcode options tuned for porta carnet (~85×54 mm). Enlarged for readability. */
const BADGE_BARCODE_OPTS = {
    format: 'CODE128',
    width: 2.2,
    height: 60,
    displayValue: true,
    fontSize: 18,
    margin: 4,
    textMargin: 2,
};

export default function VendedoresBarcodesPage() {
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [vendedores, setVendedores] = useState([]);
    const [cajeros, setCajeros] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (authLoading) return;
        if (!user || !canManageVendedorCodigos(user)) {
            navigate('/', { replace: true });
            return;
        }
        fetchVendedores();
    }, [authLoading, user, navigate]);

    const fetchVendedores = async () => {
        try {
            setLoading(true);
            const res = await fetch(`${API_URL}/api/users/vendedores/barcodes`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (res.ok) {
                const data = await res.json();
                if (Array.isArray(data)) {
                    setVendedores(data);
                    setCajeros([]);
                } else {
                    setVendedores(data.vendedores || []);
                    setCajeros(data.cajeros || []);
                }
            } else {
                setVendedores([]);
                setCajeros([]);
            }
        } catch (err) {
            console.error(err);
            setVendedores([]);
            setCajeros([]);
        } finally {
            setLoading(false);
        }
    };

    const printBarcodes = () => {
        const printWindow = window.open('', '_blank', 'width=520,height=900');
        if (!printWindow) {
            alert('Por favor habilite las ventanas emergentes para imprimir.');
            return;
        }

        const blocks = [];
        const scriptLines = [];
        const opts = JSON.stringify(BADGE_BARCODE_OPTS);

        const addPeople = (sectionTitle, sectionSub, people, prefix) => {
            if (!people.length) return;
            blocks.push(`<h2 class="section-title">${escapeHtml(sectionTitle)}</h2>`);
            blocks.push(`<p class="section-sub">${escapeHtml(sectionSub)}</p>`);
            blocks.push('<div class="badge-grid">');
            for (const v of people) {
                const sid = `${prefix}-${v.id}`;
                const code = v.codigo_barra || '';
                blocks.push(`
                        <div class="badge">
                            <div class="badge-name">${escapeHtml(v.name || 'Sin nombre')}</div>
                            <div class="badge-role">${escapeHtml(v.role_name || '')}</div>
                            <div class="barcode-wrap">
                                <svg id="bc-${sid}"></svg>
                            </div>
                        </div>`);
                scriptLines.push(`
                    try {
                        JsBarcode("#bc-${sid}", "${escapeJsString(code)}", ${opts});
                    } catch (e) { console.error(e); }`);
            }
            blocks.push('</div>');
        };

        addPeople(
            'Vendedores / preventa',
            'Formato compacto VND + ID (ej. VND42). Escanee en POS al crear preventa.',
            vendedores,
            'vnd'
        );
        addPeople(
            'Cajeros',
            'Formato CAJ + ID para porta carnet.',
            cajeros,
            'caj'
        );

        const html = `<!DOCTYPE html>
            <html>
                <head>
                    <title>Códigos — Vendedores y cajeros</title>
                    <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"></script>
                    <style>
                        @page { size: A4; margin: 8mm; }
                        * { box-sizing: border-box; }
                        body { font-family: Arial, sans-serif; margin: 0; font-size: 11px; color: #222; }
                        .header { text-align: center; margin-bottom: 10px; padding-bottom: 8px; border-bottom: 1px solid #ccc; }
                        .header h1 { margin: 0; font-size: 14px; }
                        .header p { margin: 4px 0 0; color: #666; font-size: 10px; }
                        .section-title { font-size: 12px; margin: 14px 0 4px; font-weight: bold; }
                        .section-sub { margin: 0 0 8px; color: #555; font-size: 9px; }
                        .badge-grid {
                            display: grid;
                            grid-template-columns: repeat(2, 85mm);
                            gap: 6mm 8mm;
                            justify-content: start;
                        }
                        .badge {
                            width: 85mm;
                            height: 54mm;
                            border: 1px dashed #bbb;
                            padding: 3mm 2.5mm 2mm;
                            page-break-inside: avoid;
                            display: flex;
                            flex-direction: column;
                            align-items: center;
                            text-align: center;
                            overflow: hidden;
                        }
                        .badge-name {
                            font-weight: bold;
                            font-size: 11px;
                            line-height: 1.2;
                            max-width: 100%;
                            overflow: hidden;
                            text-overflow: ellipsis;
                            white-space: nowrap;
                        }
                        .badge-role {
                            color: #666;
                            font-size: 9px;
                            margin-bottom: 2mm;
                            max-width: 100%;
                            overflow: hidden;
                            text-overflow: ellipsis;
                            white-space: nowrap;
                        }
                        .barcode-wrap {
                            flex: 1;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            width: 100%;
                            max-height: 40mm;
                        }
                        .barcode-wrap svg { width: 100%; max-width: 80mm; height: auto; }
                    </style>
                </head>
                <body>
                    <div class="header">
                        <h1>ERP Minimarket — Códigos personal</h1>
                        <p>Tarjetas 85×54 mm — recorte para porta carnet</p>
                    </div>
                    ${blocks.join('')}
                    <script>
                        window.onload = function() {
                            ${scriptLines.join('')}
                        };
                    </script>
                </body>
            </html>`;

        printWindow.document.write(html);
        printWindow.document.close();
    };

    const totalPersonas = vendedores.length + cajeros.length;

    const PersonRow = ({ v, accent }) => (
        <div className="flex items-center justify-between p-3 hover:bg-gray-50 gap-3">
            <div className="flex items-center gap-3 min-w-0">
                <div className={`w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center ${accent.bg}`}>
                    <User size={18} className={accent.icon} />
                </div>
                <div className="min-w-0">
                    <div className="font-medium text-gray-900 truncate">{v.name}</div>
                    <div className="text-xs text-gray-500 truncate">{v.role_name || '—'}</div>
                </div>
            </div>
            <div className={`font-mono text-sm font-semibold flex-shrink-0 ${accent.code}`}>
                {v.codigo_barra}
            </div>
        </div>
    );

    return (
        <div className="page p-6 max-w-4xl mx-auto">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                        <Barcode size={28} />
                        Códigos — Vendedores y cajeros
                    </h1>
                    <p className="text-gray-500 mt-1 text-sm">
                        Códigos cortos <span className="font-mono">VND123</span> / <span className="font-mono">CAJ45</span> para escaneo en POS e impresión en porta carnet.
                    </p>
                </div>
                <button
                    onClick={printBarcodes}
                    disabled={loading || totalPersonas === 0}
                    className="btn-primary flex items-center gap-2 px-5 py-2.5 text-sm"
                >
                    <Printer size={18} />
                    Imprimir carnets
                </button>
            </div>

            {loading ? (
                <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
                </div>
            ) : totalPersonas === 0 ? (
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-10 text-center">
                    <div className="bg-gray-100 p-3 rounded-full inline-flex mb-3">
                        <User size={28} className="text-gray-400" />
                    </div>
                    <h3 className="text-base font-medium text-gray-900">No hay registros para imprimir</h3>
                    <p className="text-gray-500 mt-1 text-sm">
                        Se listan usuarios activos con rol preventa/vendedor y cajeros.
                    </p>
                </div>
            ) : (
                <div className="space-y-6">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        <div className="px-4 py-2.5 bg-indigo-50 border-b border-indigo-100 font-semibold text-indigo-900 text-sm">
                            Vendedores / preventa ({vendedores.length})
                        </div>
                        <div className="divide-y divide-gray-100">
                            {vendedores.map((v) => (
                                <PersonRow
                                    key={v.id}
                                    v={v}
                                    accent={{ bg: 'bg-indigo-100', icon: 'text-indigo-600', code: 'text-indigo-600' }}
                                />
                            ))}
                        </div>
                    </div>
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        <div className="px-4 py-2.5 bg-emerald-50 border-b border-emerald-100 font-semibold text-emerald-900 text-sm">
                            Cajeros ({cajeros.length})
                        </div>
                        <div className="divide-y divide-gray-100">
                            {cajeros.map((v) => (
                                <PersonRow
                                    key={v.id}
                                    v={v}
                                    accent={{ bg: 'bg-emerald-100', icon: 'text-emerald-700', code: 'text-emerald-700' }}
                                />
                            ))}
                        </div>
                    </div>
                    <p className="text-xs text-gray-500">
                        Los códigos antiguos con ceros (ej. VND000042) siguen funcionando al escanear.
                    </p>
                </div>
            )}
        </div>
    );
}
